/**
 * v3.11.0: Agent Update Modal Component
 * v3.12.0: Corrigido cálculo de offline (usa equipment_status do backend)
 *          Removido botão Download (inútil para operador)
 * v4.0.0:  Integrado AgentUpdateProgress para mostrar barra de progresso em tempo real
 *          durante OTA updates. FIX 2026-03-04.
 * 
 * Permite atualização remota do agent via comando MQTT.
 * O agent recebe o comando, faz download, backup, instala e reinicia automaticamente.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  Check,
  AlertTriangle,
  ArrowUp,
  Package,
  Rocket,
  WifiOff,
} from 'lucide-react';

// v4.0: Import AgentUpdateProgress component
import { AgentUpdateProgress } from './AgentUpdateProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente de Badge para indicar atualização disponível
export const AgentUpdateBadge = ({ equipmentId }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(`${API}/equipments/${equipmentId}/agent-status`);
        setStatus(response.data);
      } catch (error) {
        console.error('Error fetching agent status:', error);
      } finally {
        setLoading(false);
      }
    };

    if (equipmentId) {
      fetchStatus();
    }
  }, [equipmentId]);

  if (loading || !status || !status.update_available) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50 text-xs cursor-help"
          >
            <ArrowUp className="w-3 h-3 mr-1" />
            v{status.current_version}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="text-sm">
            Atualização disponível: v{status.installed_version || 'desconhecida'} → v{status.current_version}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Modal completo de atualização do agent
const AgentUpdateModal = ({ 
  equipmentId, 
  equipmentTag,
  isOpen, 
  onClose,
}) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateSent, setUpdateSent] = useState(false);
  const [awaitingBackendConfirmation, setAwaitingBackendConfirmation] = useState(false);
  const confirmationPollRef = useRef(null);
  const fallbackPollRef = useRef(null);

  const stopConfirmationPolling = useCallback(() => {
    if (confirmationPollRef.current) {
      clearInterval(confirmationPollRef.current);
      confirmationPollRef.current = null;
    }
  }, []);

  const stopFallbackPolling = useCallback(() => {
    if (fallbackPollRef.current) {
      clearInterval(fallbackPollRef.current);
      fallbackPollRef.current = null;
    }
  }, []);

  const isUpdateConfirmed = useCallback((statusData) => {
    if (!statusData) return false;
    if (!statusData.installed_version || !statusData.current_version) return false;
    return (
      statusData.installed_version === statusData.current_version &&
      statusData.update_available === false
    );
  }, []);

  const fetchStatusSilent = useCallback(async () => {
    if (!equipmentId) return null;
    try {
      const response = await axios.get(`${API}/equipments/${equipmentId}/agent-status`, {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setStatus(response.data);
      return response.data;
    } catch (error) {
      return null;
    }
  }, [equipmentId]);

  const fetchStatus = useCallback(async () => {
    if (!equipmentId) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/equipments/${equipmentId}/agent-status`, {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache' },
      });
      setStatus(response.data);
      if (isUpdateConfirmed(response.data)) {
        // Se já está na versão atual, sai do estado de progresso/aguardando.
        setUpdateSent(false);
        setAwaitingBackendConfirmation(false);
        stopConfirmationPolling();
        stopFallbackPolling();
      }
    } catch (error) {
      console.error('Error fetching agent status:', error);
      toast.error('Erro ao verificar status do agent');
    } finally {
      setLoading(false);
    }
  }, [equipmentId, isUpdateConfirmed, stopConfirmationPolling, stopFallbackPolling]);

  const startConfirmationPolling = useCallback(() => {
    stopConfirmationPolling();
    stopFallbackPolling();
    setAwaitingBackendConfirmation(true);
    let attempts = 0;
    const maxAttempts = 30; // ~60s

    confirmationPollRef.current = setInterval(async () => {
      attempts += 1;
      const latestStatus = await fetchStatusSilent();
      if (isUpdateConfirmed(latestStatus)) {
        stopConfirmationPolling();
        setAwaitingBackendConfirmation(false);
        setUpdateSent(false);
        toast.success('Atualização confirmada no servidor');
        return;
      }

      if (attempts >= maxAttempts) {
        stopConfirmationPolling();
        setAwaitingBackendConfirmation(false);
        toast.warning('Atualização concluída no equipamento, mas confirmação do servidor está lenta. Use atualizar informações.');
      }
    }, 2000);
  }, [fetchStatusSilent, isUpdateConfirmed, stopConfirmationPolling, stopFallbackPolling]);

  const startFallbackPolling = useCallback(() => {
    stopFallbackPolling();
    let attempts = 0;
    const maxAttempts = 30; // ~60s

    fallbackPollRef.current = setInterval(async () => {
      attempts += 1;
      const latestStatus = await fetchStatusSilent();
      if (isUpdateConfirmed(latestStatus)) {
        setUpdateSent(false);
        setAwaitingBackendConfirmation(false);
        stopConfirmationPolling();
        stopFallbackPolling();
        toast.success('Atualização confirmada no servidor');
        return;
      }
      if (attempts >= maxAttempts) {
        stopFallbackPolling();
      }
    }, 2000);
  }, [fetchStatusSilent, isUpdateConfirmed, stopConfirmationPolling, stopFallbackPolling]);

  const handleProgressEvent = useCallback((event) => {
    if (!event || event.device_id !== (status?.equipment_tag || equipmentTag || '').toLowerCase()) {
      return;
    }
    if (event.status === 'success') {
      startConfirmationPolling();
    }
    if (event.status === 'error' || event.status === 'rolled_back') {
      stopConfirmationPolling();
      setAwaitingBackendConfirmation(false);
      stopFallbackPolling();
    }
  }, [equipmentTag, startConfirmationPolling, status?.equipment_tag, stopConfirmationPolling, stopFallbackPolling]);

  useEffect(() => {
    if (isOpen && equipmentId) {
      fetchStatus();
      setUpdateSent(false);
      setAwaitingBackendConfirmation(false);
      stopConfirmationPolling();
      stopFallbackPolling();
    }
    if (!isOpen) {
      setUpdateSent(false);
      setAwaitingBackendConfirmation(false);
      stopConfirmationPolling();
      stopFallbackPolling();
    }
  }, [isOpen, equipmentId, fetchStatus, stopConfirmationPolling, stopFallbackPolling]);

  useEffect(() => {
    return () => {
      stopConfirmationPolling();
      stopFallbackPolling();
    };
  }, [stopConfirmationPolling, stopFallbackPolling]);

  // Trigger remote update via MQTT
  const handleTriggerUpdate = async () => {
    setUpdating(true);
    
    try {
      const response = await axios.post(`${API}/equipments/${equipmentId}/trigger-update`);
      
      toast.success(response.data.message || 'Comando de atualização enviado!');
      setUpdateSent(true);
      setAwaitingBackendConfirmation(false);
      startFallbackPolling();
      
    } catch (error) {
      console.error('Error triggering update:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao enviar comando de atualização';
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  // FIX v3.12.0: Usar equipment_status do backend (Camada 2 é autoritativa)
  // Removido cálculo local que causava oscilação por timezone/clock skew
  const isOffline = status?.equipment_status === 'offline';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-slate-900 border-slate-700" data-testid="agent-update-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-400" />
            Atualização do Agent
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {equipmentTag || equipmentId}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : status ? (
          <div className="space-y-4 py-4">
            {/* Version Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Versão Instalada</p>
                <p className="text-lg font-mono">
                  {status.installed_version || (
                    <span className="text-yellow-400">Não reportada</span>
                  )}
                </p>
              </div>
              <div className="p-3 bg-slate-800 rounded-lg">
                <p className="text-xs text-muted-foreground mb-1">Versão Disponível</p>
                <p className="text-lg font-mono text-green-400">
                  {status.current_version}
                </p>
              </div>
            </div>

            {/* Update Status Messages - Ordem de precedência corrigida */}
            {/* v4.0: Quando update enviado, mostra AgentUpdateProgress em tempo real */}
            {updateSent && status?.update_available !== false ? (
              <>
                <AgentUpdateProgress 
                  deviceId={(status?.equipment_tag || equipmentTag || equipmentId || '').toLowerCase()} 
                  onRetry={handleTriggerUpdate}
                  fallbackToVersion={status?.current_version}
                  onProgressEvent={handleProgressEvent}
                  compact={false}
                />
                {awaitingBackendConfirmation && (
                  <Alert className="bg-blue-500/10 border-blue-500/30">
                    <RefreshCw className="h-4 w-4 text-blue-400 animate-spin" />
                    <AlertDescription className="text-sm text-blue-300">
                      Atualização aplicada no equipamento. Aguardando confirmação do servidor...
                    </AlertDescription>
                  </Alert>
                )}
              </>
            ) : isOffline ? (
              <Alert className="bg-red-500/10 border-red-500/30">
                <WifiOff className="h-4 w-4 text-red-400" />
                <AlertDescription className="text-sm text-red-300">
                  Equipamento offline. Aguarde o equipamento ficar online para 
                  enviar o comando de atualização remota.
                </AlertDescription>
              </Alert>
            ) : !status.installed_version && status.update_available ? (
              // FIX v3.12.0: Mensagem específica quando versão não reportada
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <AlertTriangle className="h-4 w-4 text-yellow-400" />
                <AlertDescription className="text-sm text-yellow-300">
                  Versão instalada não reportada. Clique em "Atualizar Agent" para 
                  enviar a versão mais recente ({status.current_version}).
                </AlertDescription>
              </Alert>
            ) : status.update_available ? (
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <ArrowUp className="h-4 w-4 text-yellow-400" />
                <AlertDescription className="text-sm text-yellow-300">
                  Nova versão disponível! Clique em "Atualizar Agent" para 
                  enviar o comando de atualização remota.
                </AlertDescription>
              </Alert>
            ) : status.installed_version ? (
              <Alert className="bg-green-500/10 border-green-500/30">
                <Check className="h-4 w-4 text-green-400" />
                <AlertDescription className="text-sm text-green-300">
                  O agent está na versão mais recente.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-slate-500/10 border-slate-500/30">
                <AlertTriangle className="h-4 w-4 text-slate-400" />
                <AlertDescription className="text-sm text-slate-300">
                  Aguardando dados de telemetria do equipamento.
                </AlertDescription>
              </Alert>
            )}

            {/* How it works - only show if update available */}
            {status.update_available && !updateSent && (
              <div className="text-sm text-muted-foreground space-y-2 bg-slate-800/50 p-3 rounded-lg">
                <p className="font-medium">Como funciona:</p>
                <ol className="list-decimal list-inside space-y-1 pl-2">
                  <li>Comando enviado via MQTT para o equipamento</li>
                  <li>Agent baixa a nova versão automaticamente</li>
                  <li>Backup da versão atual é criado</li>
                  <li>Nova versão instalada e serviço reiniciado</li>
                </ol>
              </div>
            )}
          </div>
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Erro ao carregar informações do agent
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          <div className="flex-1" />
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={fetchStatus} 
            disabled={loading}
            title="Atualizar informações"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {/* FIX v3.12.0: Botão Atualizar Agent sempre visível quando update_available */}
          {status?.update_available && !isOffline && !updateSent && (
            <Button 
              onClick={handleTriggerUpdate} 
              disabled={updating}
              data-testid="trigger-update-btn"
              className="bg-green-600 hover:bg-green-700"
            >
              <Rocket className="w-4 h-4 mr-2" />
              {updating ? 'Enviando...' : 'Atualizar Agent'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AgentUpdateModal;
